"use client";

import type { HTMLAttributes } from "react";
import { memo } from "react";
import ReactMarkdown, { type Options } from "react-markdown";
import rehypeKatex from "rehype-katex";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import "katex/dist/katex.min.css";
import hardenReactMarkdown from "harden-react-markdown";
import { cn } from "@drpcrd/utils/shared";
import { Typography } from "../typography";

/**
 * Handle incomplete links and images
 * Pattern: [...] or ![...] where the closing ] is missing
 */
const handleIncompleteLinks = (input: string): string => {
  // Handle incomplete links and images
  // Pattern: [...] or ![...] where the closing ] is missing
  const linkImagePattern = /(!?\[)([^\]]*?)$/;
  const linkMatch = input.match(linkImagePattern);
  if (linkMatch) {
    // If we have an unterminated [ or ![, remove it and everything after
    const startIndex = input.lastIndexOf(linkMatch[1]);
    return input.substring(0, startIndex);
  }
  return input;
};

/**
 * Handle incomplete bold formatting (**)
 */
const handleIncompleteBold = (input: string): string => {
  // Handle incomplete bold formatting (**)
  const boldPattern = /(\*\*)([^*]*?)$/;
  const boldMatch = input.match(boldPattern);
  if (boldMatch) {
    // Count the number of ** in the entire string
    const asteriskPairs = (input.match(/\*\*/g) || []).length;
    // If odd number of **, we have an incomplete bold - complete it
    if (asteriskPairs % 2 === 1) {
      return `${input}**`;
    }
  }
  return input;
};

/**
 * Handle incomplete italic formatting (__)
 */
const handleIncompleteItalicUnderscore = (input: string): string => {
  // Handle incomplete italic formatting (__)
  const italicPattern = /(__)([^_]*?)$/;
  const italicMatch = input.match(italicPattern);
  if (italicMatch) {
    // Count the number of __ in the entire string
    const underscorePairs = (input.match(/__/g) || []).length;
    // If odd number of __, we have an incomplete italic - complete it
    if (underscorePairs % 2 === 1) {
      return `${input}__`;
    }
  }
  return input;
};

/**
 * Handle incomplete single asterisk italic (*) - only if it looks like incomplete formatting
 */
const handleIncompleteSingleAsterisk = (input: string): string => {
  // Handle incomplete single asterisk italic (*) - only if it looks like incomplete formatting
  const singleAsteriskPattern = /(\*)([^*\s]*?)$/;
  const singleAsteriskMatch = input.match(singleAsteriskPattern);
  if (singleAsteriskMatch) {
    // Only complete if the asterisk appears to be starting italic formatting
    // (i.e., it's followed by non-whitespace content and not at word boundaries)
    const lastAsteriskIndex = input.lastIndexOf("*");
    const textAfterAsterisk = input.substring(lastAsteriskIndex + 1);

    // Only complete if there's text after the asterisk and it doesn't look like a natural ending
    const looksLikeIncompleteItalic =
      textAfterAsterisk.length > 0 &&
      !textAfterAsterisk.match(/^\s*$/) && // not just whitespace
      !input.match(/\*\s*$/) && // doesn't end with "* " (bullet point)
      textAfterAsterisk.length < 50; // reasonable length for italic text

    if (looksLikeIncompleteItalic) {
      // Count single asterisks that aren't part of **
      const singleAsterisks = input.split("").reduce((acc, char, index) => {
        if (char === "*") {
          // Check if it's part of a ** pair
          const prevChar = input[index - 1];
          const nextChar = input[index + 1];
          if (prevChar !== "*" && nextChar !== "*") {
            return acc + 1;
          }
        }
        return acc;
      }, 0);

      // If odd number of single *, we have an incomplete italic - complete it
      if (singleAsterisks % 2 === 1) {
        return `${input}*`;
      }
    }
  }
  return input;
};

/**
 * Handle incomplete single underscore italic (_) - only if it looks like incomplete formatting
 */
const handleIncompleteSingleUnderscore = (input: string): string => {
  // Handle incomplete single underscore italic (_) - only if it looks like incomplete formatting
  const singleUnderscorePattern = /(_)([^_\s]*?)$/;
  const singleUnderscoreMatch = input.match(singleUnderscorePattern);
  if (singleUnderscoreMatch) {
    // Only complete if the underscore appears to be starting italic formatting
    const lastUnderscoreIndex = input.lastIndexOf("_");
    const textAfterUnderscore = input.substring(lastUnderscoreIndex + 1);

    // Only complete if there's text after the underscore and it doesn't look like a natural ending
    const looksLikeIncompleteItalic =
      textAfterUnderscore.length > 0 &&
      !textAfterUnderscore.match(/^\s*$/) && // not just whitespace
      textAfterUnderscore.length < 50; // reasonable length for italic text

    if (looksLikeIncompleteItalic) {
      // Count single underscores that aren't part of __
      const singleUnderscores = input.split("").reduce((acc, char, index) => {
        if (char === "_") {
          // Check if it's part of a __ pair
          const prevChar = input[index - 1];
          const nextChar = input[index + 1];
          if (prevChar !== "_" && nextChar !== "_") {
            return acc + 1;
          }
        }
        return acc;
      }, 0);

      // If odd number of single _, we have an incomplete italic - complete it
      if (singleUnderscores % 2 === 1) {
        return `${input}_`;
      }
    }
  }
  return input;
};

/**
 * Handle incomplete inline code blocks (`) - but avoid code blocks (```)
 */
const handleIncompleteInlineCode = (input: string): string => {
  // Handle incomplete inline code blocks (`) - but avoid code blocks (```)
  const inlineCodePattern = /(`)([^`]*?)$/;
  const inlineCodeMatch = input.match(inlineCodePattern);
  if (inlineCodeMatch) {
    // Check if we're dealing with a code block (triple backticks)
    const allTripleBackticks = (input.match(/```/g) || []).length;

    // If we have an odd number of ``` sequences, we're inside an incomplete code block
    // In this case, don't complete inline code
    const insideIncompleteCodeBlock = allTripleBackticks % 2 === 1;

    if (!insideIncompleteCodeBlock) {
      // Count the number of single backticks that are NOT part of triple backticks
      const singleBacktickCount = input.split("").reduce((count, char, i) => {
        if (char === "`") {
          // Check if this backtick is part of a triple backtick sequence
          const isTripleStart = input.substring(i, i + 3) === "```";
          const isTripleMiddle =
            i > 0 && input.substring(i - 1, i + 2) === "```";
          const isTripleEnd = i > 1 && input.substring(i - 2, i + 1) === "```";

          if (!(isTripleStart || isTripleMiddle || isTripleEnd)) {
            return count + 1;
          }
        }
        return count;
      }, 0);

      // If odd number of single backticks, we have an incomplete inline code - complete it
      if (singleBacktickCount % 2 === 1) {
        return `${input}\``;
      }
    }
  }
  return input;
};

/**
 * Handle incomplete strikethrough formatting (~~)
 */
const handleIncompleteStrikethrough = (input: string): string => {
  // Handle incomplete strikethrough formatting (~~)
  const strikethroughPattern = /(~~)([^~]*?)$/;
  const strikethroughMatch = input.match(strikethroughPattern);
  if (strikethroughMatch) {
    // Count the number of ~~ in the entire string
    const tildePairs = (input.match(/~~/g) || []).length;
    // If odd number of ~~, we have an incomplete strikethrough - complete it
    if (tildePairs % 2 === 1) {
      return `${input}~~`;
    }
  }
  return input;
};

/**
 * Chain of transformations to handle different markdown patterns
 */
const markdownTransformations = [
  handleIncompleteLinks,
  handleIncompleteBold,
  handleIncompleteItalicUnderscore,
  handleIncompleteSingleAsterisk,
  handleIncompleteSingleUnderscore,
  handleIncompleteInlineCode,
  handleIncompleteStrikethrough,
] as const;

/**
 * Models drop raw <br> tags inside markdown table cells (GFM cells can't
 * hold real line breaks). Raw HTML renders as literal text here — by
 * design, it's never executed — so swap breaks for a separator instead
 * of showing "<br>" to the user.
 */
const replaceRawLineBreaks = (text: string): string =>
  text.replace(/<br\s*\/?>/gi, " · ");

/**
 * Parses markdown text and removes incomplete tokens to prevent partial rendering
 * of links, images, bold, and italic formatting during streaming.
 */
const parseIncompleteMarkdown = (text: string): string => {
  if (!text || typeof text !== "string") {
    return text;
  }

  // Chain all transformations together
  return markdownTransformations.reduce(
    (result, transform) => transform(result),
    text,
  );
};

// Create a hardened version of ReactMarkdown
const HardenedMarkdown = hardenReactMarkdown(ReactMarkdown);

export type ResponseProps = HTMLAttributes<HTMLDivElement> & {
  options?: Options;
  children: Options["children"];
  allowedImagePrefixes?: string[];
  allowedLinkPrefixes?: string[];
  defaultOrigin?: string;
  parseIncompleteMarkdown?: boolean;
};

const components: Options["components"] = {
  ol: ({ children, className }) => (
    <ol className={cn("ml-4 list-outside list-decimal", className)}>
      {children}
    </ol>
  ),

  li: ({ children, className }) => (
    <li className={cn("pb-1 [&_p]:mb-0", className)}>
      <Typography as="span" size="sm" className="whitespace-normal">
        {children}
      </Typography>
    </li>
  ),
  ul: ({ children, className }) => (
    <ul className={cn("ml-4 list-outside list-disc", className)}>{children}</ul>
  ),
  hr: ({ className }) => (
    <hr className={cn("my-2 border-neutral-200", className)} />
  ),
  strong: ({ children, className }) => (
    <span className={cn("font-semibold", className)}>{children}</span>
  ),
  a: ({ children, className, href, target, rel }) => (
    // TODO: This needs to be modified to use an actual next.js Link
    <a
      className={cn("text-primary font-medium underline", className)}
      href={href}
      rel={rel || "noreferrer"}
      target={target || "_blank"}
    >
      {children}
    </a>
  ),
  h1: ({ children, className }) => (
    <Typography
      as="h1"
      size="base"
      weight="semibold"
      className={cn("mb-2", className)}
    >
      {children}
    </Typography>
  ),
  h2: ({ children, className }) => (
    <Typography
      as="h2"
      size="base"
      weight="semibold"
      className={cn("mb-2", className)}
    >
      {children}
    </Typography>
  ),
  h3: ({ children, className }) => (
    <Typography
      as="h3"
      size="base"
      weight="semibold"
      className={cn("mb-2", className)}
    >
      {children}
    </Typography>
  ),
  h4: ({ children, className }) => (
    <Typography
      as="h4"
      size="base"
      weight="semibold"
      className={cn("mb-2", className)}
    >
      {children}
    </Typography>
  ),
  h5: ({ children, className }) => (
    <Typography
      as="h5"
      size="base"
      weight="semibold"
      className={cn("mb-2", className)}
    >
      {children}
    </Typography>
  ),
  h6: ({ children, className }) => (
    <Typography
      as="h6"
      size="base"
      weight="semibold"
      className={cn("mb-2", className)}
    >
      {children}
    </Typography>
  ),
  p: ({ children, className }) => (
    <Typography as="p" size="sm" className={cn("mb-1", className)}>
      {children}
    </Typography>
  ),
  table: ({ children, className }) => (
    <div className="border-primary-50 my-2 overflow-x-auto rounded-2xl border bg-white">
      <table
        className={cn(
          "w-max min-w-full border-collapse whitespace-normal",
          className,
        )}
      >
        {children}
      </table>
    </div>
  ),
  thead: ({ children, className }) => (
    <thead
      className={cn("bg-primary-100 border-primary-50 border-b", className)}
    >
      {children}
    </thead>
  ),
  tbody: ({ children, className }) => (
    <tbody className={cn("divide-primary-50 divide-y", className)}>
      {children}
    </tbody>
  ),
  tr: ({ children, className }) => <tr className={className}>{children}</tr>,
  th: ({ children, className }) => (
    <th className={cn("px-1.5 py-1 text-left", className)}>
      <Typography
        as="span"
        size="sm"
        weight="semibold"
        className="block max-w-70 whitespace-normal"
      >
        {children}
      </Typography>
    </th>
  ),
  td: ({ children, className }) => (
    <td className={cn("px-1.5 py-1 align-top", className)}>
      <Typography
        as="span"
        size="sm"
        className="block max-w-70 whitespace-normal"
      >
        {children}
      </Typography>
    </td>
  ),
  blockquote: ({ children, className }) => (
    <blockquote
      className={cn(
        "my-2 border-l-4 border-neutral-200 pl-2 text-neutral-900 italic",
        className,
      )}
    >
      {children}
    </blockquote>
  ),
  // Inline code renders as plain emphasised text — returning null here would
  // silently swallow anything the model backticks (e.g. @handles), leaving
  // empty "()" in the prose. Block code (pre) stays hidden.
  code: ({ children, className }) => (
    <span className={cn("font-medium", className)}>{children}</span>
  ),
  pre: () => null,
};

export const ChatResponse = memo(
  ({
    className,
    options,
    children,
    allowedImagePrefixes,
    allowedLinkPrefixes,
    defaultOrigin,
    parseIncompleteMarkdown: shouldParseIncompleteMarkdown = true,
    ...props
  }: ResponseProps) => {
    const withoutRawBreaks =
      typeof children === "string" ? replaceRawLineBreaks(children) : children;

    // Parse the children to remove incomplete markdown tokens if enabled
    const parsedChildren =
      typeof withoutRawBreaks === "string" && shouldParseIncompleteMarkdown
        ? parseIncompleteMarkdown(withoutRawBreaks)
        : withoutRawBreaks;

    return (
      <div
        className={cn(
          "size-full [&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
          className,
        )}
        {...props}
      >
        <HardenedMarkdown
          allowedImagePrefixes={allowedImagePrefixes ?? ["*"]}
          allowedLinkPrefixes={allowedLinkPrefixes ?? ["*"]}
          components={components}
          defaultOrigin={defaultOrigin}
          rehypePlugins={[rehypeKatex]}
          remarkPlugins={[remarkGfm, remarkMath]}
          {...options}
        >
          {parsedChildren}
        </HardenedMarkdown>
      </div>
    );
  },
  (prevProps, nextProps) => prevProps.children === nextProps.children,
);

ChatResponse.displayName = "ChatResponse";
